import React, { useEffect, useState, useRef } from 'react';
import { usePlayer, StreamStatus } from '../../contexts/PlayerContext';
import { liveStream } from '../../assets/metadata/episodes';
import AdaptiveMediaPlayer from '../../components/media/AdaptiveMediaPlayer';
import EpisodePlayer from '../../components/media/EpisodePlayer';
import { useSanity } from '../../hooks/useSanity.js';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Clock, Calendar, Users, Play, Filter, Search, X, Radio, Info, Music, Headphones, ChevronDown, ChevronUp, VolumeX, Volume2, Pause, Maximize2 } from 'lucide-react';
import logoFile from '../../assets/cards/logo192.png';

// Episode Card Component with independent player
const EpisodeCard = ({ episode, index, formatDuration }) => {
  const [showPlayer, setShowPlayer] = useState(false);
  
  return (
    <motion.div
      key={episode._id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col"
      dir="rtl"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={episode.imageUrl || episode.show.imageUrl || '/placeholder-episode.jpg'}
          alt={episode.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-4">
          <div className="bg-[#40B8BF] text-white px-2 py-0.5 text-xs rounded-md inline-block mb-2 w-fit">
            {episode.show.title}
          </div>
          <h3 className="text-white font-bold truncate text-right">{episode.title}</h3>
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex-1">
          {episode.description && (
            <p className="content-text text-[#393737]/80 line-clamp-2 mb-3 text-right">
              {episode.description}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mb-4 justify-end">
            {episode.recordingDate && (
              <span className="flex items-center gap-1 bg-[#784BCB]/10 px-2 py-0.5 rounded-md text-xs text-[#784BCB]">
                <Calendar className="w-3 h-3" />
                {new Date(episode.recordingDate).toLocaleDateString('he-IL')}
              </span>
            )}
            {episode.duration && (
              <span className="flex items-center gap-1 bg-[#40B8BF]/10 px-2 py-0.5 rounded-md text-xs text-[#40B8BF]">
                <Clock className="w-3 h-3" />
                {formatDuration(episode.duration)}
              </span>
            )}
          </div>
        </div>
        
        {/* Player Toggle Button */}
        <motion.button
          onClick={() => setShowPlayer(!showPlayer)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`
            w-full flex items-center justify-center gap-2 py-2.5 mb-3 rounded-md 
            text-white font-medium transition-all app-button
            ${showPlayer 
              ? 'bg-[#40B8BF] hover:bg-[#35A4AA]' 
              : 'bg-gradient-to-l from-[#784BCB] to-[#E15E92] hover:from-[#6A3DB5] hover:to-[#D04982]'}
          `}
        >
          {showPlayer ? (
            <>
              <ChevronUp className="w-4 h-4" />
              <span>הסתר נגן</span>
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              <span>נגן עכשיו</span>
            </>
          )}
        </motion.button>
        
        {/* Episode Player */}
        <AnimatePresence>
          {showPlayer && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-3 overflow-hidden"
            >
              <EpisodePlayer episode={episode} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Featured Episode Card Component with independent player
const FeaturedEpisodeCard = ({ episode, formatDuration }) => {
  const [showPlayer, setShowPlayer] = useState(false);
  
  return (
    <motion.div
      key={episode._id}
      initial={{ scale: 0.98, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.98, opacity: 0 }}
      className="mb-8 bg-white rounded-lg shadow-md overflow-hidden"
      dir="rtl"
    >
      <div className="flex flex-col md:flex-row-reverse">
        <div className="w-full md:w-2/5 relative h-60 md:h-auto overflow-hidden">
          <img 
            src={episode.imageUrl || episode.show.imageUrl || '/placeholder-episode.jpg'} 
            alt={episode.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent md:bg-gradient-to-l md:from-black/60 md:via-black/30 md:to-transparent"></div>
          <div className="absolute top-4 right-4 bg-[#40B8BF] text-white px-3 py-1 text-sm rounded-md">
            {episode.show.title}
          </div>
        </div>
        <div className="p-6 flex-1 md:relative">
          <h3 className="text-xl md:text-2xl font-bold text-[#393737] mb-2 text-right">
            פרק {episode.episodeNumber || '1'}: {episode.title}
          </h3>
          {episode.description && (
            <p className="content-text text-[#393737]/80 line-clamp-3 mb-4 text-right">
              {episode.description}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-4 text-[#393737]/70 text-sm mb-4 justify-end">
            <div className="flex items-center gap-1 bg-[#784BCB]/10 px-2 py-1 rounded-md">
              <Calendar className="w-3 h-3 text-[#784BCB]" />
              <span>
                {episode.recordingDate 
                  ? new Date(episode.recordingDate).toLocaleDateString('he-IL')
                  : 'אין תאריך'}
              </span>
            </div>
            <div className="flex items-center gap-1 bg-[#40B8BF]/10 px-2 py-1 rounded-md">
              <Clock className="w-3 h-3 text-[#40B8BF]" />
              <span>{formatDuration(episode.duration)}</span>
            </div>
            {episode.guests?.length > 0 && (
              <div className="flex items-center gap-1 bg-[#E15E92]/10 px-2 py-1 rounded-md">
                <Users className="w-3 h-3 text-[#E15E92]" />
                <span>אורחים: {episode.guests.join(', ')}</span>
              </div>
            )}
          </div>
          
          {/* Player Toggle Button */}
          <div className="flex justify-end">
            <motion.button
              onClick={() => setShowPlayer(!showPlayer)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`
                inline-flex items-center gap-2 px-6 py-3 rounded-md 
                text-white font-medium app-button
                ${showPlayer 
                  ? 'bg-[#40B8BF] hover:bg-[#35A4AA]' 
                  : 'bg-gradient-to-l from-[#784BCB] to-[#E15E92] hover:from-[#6A3DB5] hover:to-[#D04982]'}
              `}
            >
              {showPlayer ? (
                <>
                  <ChevronUp className="w-5 h-5" />
                  <span>הסתר נגן</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>נגן עכשיו</span>
                </>
              )}
            </motion.button>
          </div>
          
          {/* Episode Player */}
          <AnimatePresence>
            {showPlayer && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 overflow-hidden"
              >
                <EpisodePlayer episode={episode} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default function HomePage() {
  const { playLiveStream, prepareLiveStream, stop, state, currentTrack, playTrack } = usePlayer();
  const { shows, loading, error } = useSanity();
  const [selectedShow, setSelectedShow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const isLiveOnline = state.streamStatus === StreamStatus.ONLINE;
  const [visibleEpisodeCount, setVisibleEpisodeCount] = useState(7);
  
  // Refs for scroll sections
  const liveStreamRef = useRef(null);
  const vodRef = useRef(null);
  const infoRef = useRef(null);
  const upcomingShowsRef = useRef(null);
  const latestEpisodesRef = useRef(null);
  const featuredArtistsRef = useRef(null);

  // Scroll animations
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.3]);

  useEffect(() => {
    // Prepare live stream when component mounts, but don't play automatically
    if (isLiveOnline) {
      prepareLiveStream(liveStream.url);
    }

    // Cleanup when component unmounts
    return () => {
      stop();
    };
  }, [prepareLiveStream, stop, isLiveOnline]);

  // Handle manual play of the live stream
  const handleStartLiveStream = () => {
    playLiveStream(liveStream.url);
  };

  const handlePlay = (episode, show) => {
    if (!episode.audioUrl) {
      console.error('No audio URL available for episode:', episode.title);
      return;
    }

    playTrack({
      id: episode._id,
      title: episode.title,
      audioUrl: episode.audioUrl,
      imageUrl: episode.imageUrl || show.imageUrl,
      showTitle: show.title
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Filter and search logic for VOD
  const filteredEpisodes = React.useMemo(() => {
    if (!shows || shows.length === 0) return [];
    
    const allEpisodes = shows.flatMap(show => 
      (show.episodes || []).map(episode => ({
        ...episode,
        show: {
          ...show,
          _id: show._id,
          title: show.title,
          imageUrl: show.imageUrl
        }
      }))
    );

    return allEpisodes.filter(episode => {
      const matchesShow = !selectedShow || episode.show._id === selectedShow._id;
      const matchesSearch = searchTerm.trim() === '' || 
        episode.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        episode.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        episode.show.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        episode.guests?.some(guest => 
          guest.toLowerCase().includes(searchTerm.toLowerCase())
        );

      return matchesShow && matchesSearch;
    }).sort((a, b) => new Date(b.recordingDate) - new Date(a.recordingDate));
  }, [shows, selectedShow, searchTerm]);

  // Split episodes for featured and grid display
  const featuredEpisode = filteredEpisodes[0];
  const displayedEpisodes = filteredEpisodes.slice(1, visibleEpisodeCount);

  // Show more episodes function
  const handleShowMoreEpisodes = () => {
    setVisibleEpisodeCount(prev => prev + 6); // Show 6 more episodes
  };

  // Function to navigate to card sections within the display window
  const navigateToCardSection = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center min-h-[400px]"
      >
        <div className="animate-pulse text-lg">טוען תכנים...</div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center text-red-500 p-4"
      >
        <h3 className="text-lg font-bold mb-2">שגיאה בטעינת התוכן</h3>
        <p className="mb-4">{error.message}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 px-4 py-2 bg-[#784BCB] text-white rounded hover:bg-[#6A3DB5] app-button"
        >
          נסה שוב
        </button>
      </motion.div>
    );
  }

  const scrollToInfo = () => {
    if (infoRef.current) {
      infoRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToShowcase = () => {
    if (liveStreamRef.current) {
      liveStreamRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="space-y-6 px-4 md:px-6 max-w-7xl mx-auto">
      {/* Live Stream Section */}
      <section ref={liveStreamRef} className="pt-8 pb-4 bg-gradient-to-r from-[#784BCB]/5 to-[#E15E92]/5 backdrop-blur-sm rounded-xl shadow-sm">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Left column: Player */}
          <div className="w-full md:w-7/12">
            {/* Status indicator with force play button - outside player */}
            <div className="flex justify-center mb-3">
              <div className="flex items-center gap-2">
                <div className={`
                  relative group
                  inline-flex items-center gap-3 px-4 py-2
                  rounded-xl transition-all duration-300
                  before:absolute before:inset-0 before:rounded-xl before:transition-all
                  before:border before:border-white/20 before:backdrop-blur-sm
                  ${isLiveOnline 
                    ? 'before:bg-gradient-to-r before:from-[#E15E92]/20 before:to-[#784BCB]/20 text-[#E15E92]' 
                    : 'before:bg-white/5 text-gray-400'
                  }
                  hover:before:border-white/30
                `}>
                  <div className="relative flex items-center justify-center w-7 h-7">
                    <div className={`
                      absolute inset-0 rounded-full transition-all duration-300
                      ${isLiveOnline 
                        ? 'bg-gradient-to-r from-[#E15E92]/20 to-[#784BCB]/20 group-hover:from-[#E15E92]/30 group-hover:to-[#784BCB]/30' 
                        : 'bg-white/5 group-hover:bg-white/10'
                      }
                    `} />
                    <div className={`
                      w-2 h-2 rounded-full relative z-10 transition-transform duration-300
                      group-hover:scale-110
                      ${isLiveOnline 
                        ? 'bg-gradient-to-r from-[#E15E92] to-[#784BCB]' 
                        : 'bg-gray-400'
                      }
                    `} />
                    {isLiveOnline && (
                      <>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#E15E92] to-[#784BCB] animate-ping opacity-50" />
                        <div className="absolute -inset-3 rounded-full bg-gradient-to-r from-[#E15E92]/10 to-[#784BCB]/10 animate-pulse" />
                      </>
                    )}
                  </div>
                  <span className="relative font-medium tracking-wide text-sm">
                    {isLiveOnline ? 'שידור חי' : 'אין שידור כעת'}
                  </span>
                </div>

                {/* Force play button - next to status indicator when offline */}
                {!isLiveOnline && (
                  <button
                    onClick={handleStartLiveStream}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#E15E92] text-white rounded-xl 
                             hover:bg-[#D04982] transition-colors relative overflow-hidden"
                  >
                    <Play className="w-4 h-4" />
                    <span className="text-sm font-medium">נסה להתחבר</span>
                  </button>
                )}
              </div>
            </div>

            {/* Player with maintained aspect ratio and enhanced design */}
            <motion.div 
              className="relative rounded-xl overflow-hidden shadow-lg"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="absolute inset-0 pointer-events-none z-10 bg-gradient-to-t from-black/20 to-transparent"></div>
              
              <AdaptiveMediaPlayer mode="video" />
              
              {/* Play button overlay - only show when online and not playing */}
              {isLiveOnline && !state.isPlaying && (
                <div 
                  className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer z-20"
                  onClick={handleStartLiveStream}
                >
                  <motion.div 
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-[#E15E92] text-white p-4 rounded-full shadow-xl"
                  >
                    <Play className="w-10 h-10" />
                  </motion.div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Right column: Info with glassmorphism */}
          <div className="w-full md:w-5/12">
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -5 }}
              className="rounded-xl p-8 shadow-lg h-full bg-white/60 backdrop-blur-sm border border-white/20 relative overflow-hidden"
              dir="rtl"
            >
              <div className="absolute inset-0 bg-gradient-radial from-white/40 to-transparent opacity-80 pointer-events-none"></div>
              
              <h2 className="text-2xl font-bold text-[#E15E92] mb-4 relative text-right">
                <span className="relative">
                  רדיו שבלולים
                  <span className="absolute -bottom-1 right-0 w-full h-[2px] bg-gradient-to-r from-[#E15E92] to-[#784BCB]"></span>
                </span>
              </h2>
              <div className="text-[#393737] relative">
                <p className="mb-4 font-medium text-right font-content">
                  רדיו שבלולים — תחנה עצמאית לשידורי מוזיקה, שיחות ותרבות.
                </p>
                <p className="text-[#393737]/80 text-right font-content">
                  📻🐚 מצטרפים אלינו לשידור חי של מוזיקה ושיחות מרתקות 📻🐚
                </p>
                
                {/* Center the logo wrapper */}
                <div className="flex justify-center mt-6">
                  <motion.div
                    className="relative w-40 h-40"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    {/* Decorative elements */}
                    <motion.div
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-[#784BCB]/20 to-[#E15E92]/20 blur-xl"
                      animate={{ 
                        scale: [1, 1.1, 1],
                        opacity: [0.7, 0.9, 0.7] 
                      }}
                      transition={{ 
                        duration: 4,
                        repeat: Infinity,
                        repeatType: "reverse" 
                      }}
                    />
                    
                    <motion.div 
                      className="absolute inset-0 flex items-center justify-center"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                    >
                      <div className="w-full h-full rounded-full border-4 border-dashed border-[#784BCB]/30" />
                    </motion.div>
                    
                    {/* Main logo */}
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      whileHover={{ scale: 1.05, rotate: 5 }}
                      animate={{ 
                        y: [0, -10, 0],
                      }}
                      transition={{ 
                        duration: 5,
                        repeat: Infinity,
                        repeatType: "reverse" 
                      }}
                    >
                      <img 
                        src={logoFile} 
                        alt="רדיו שבלולים" 
                        className="w-32 h-32 drop-shadow-xl" 
                      />
                    </motion.div>

                    {/* Small decorative elements */}
                    <motion.div
                      className="absolute top-0 right-0 w-6 h-6 rounded-full bg-[#40B8BF]/80"
                      animate={{ 
                        y: [0, 15, 0],
                        x: [0, -10, 0],
                        scale: [1, 1.1, 1],
                      }}
                      transition={{ 
                        duration: 6,
                        repeat: Infinity,
                        repeatType: "reverse" 
                      }}
                    />
                    
                    <motion.div
                      className="absolute bottom-5 left-0 w-8 h-8 rounded-full bg-[#E15E92]/80"
                      animate={{ 
                        y: [0, -15, 0],
                        x: [0, 10, 0],
                        scale: [1, 1.2, 1],
                      }}
                      transition={{ 
                        duration: 7,
                        repeat: Infinity,
                        repeatType: "reverse" 
                      }}
                    />
                  </motion.div>
                </div>
                
                {/* Now playing display */}
                {currentTrack && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-4 rounded-lg bg-gradient-to-r from-[#784BCB]/10 to-[#E15E92]/10 border border-white/30"
                  >
                    <div className="flex items-center gap-3 justify-end">
                      <span className="font-medium text-sm">מתנגן עכשיו:</span>
                      <Music className="text-[#E15E92] w-5 h-5" />
                    </div>
                    <p className="font-bold mt-1 text-[#393737] text-right">{currentTrack.title}</p>
                    {currentTrack.showTitle && (
                      <p className="text-sm text-[#393737]/70 mt-0.5 text-right">{currentTrack.showTitle}</p>
                    )}
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        <div className="h-1 bg-gradient-to-r from-[#784BCB] to-[#E15E92] mt-4 rounded-full shadow-sm" />
      </section>

      {/* Information Section */}
      <section ref={infoRef} className="py-4 bg-gradient-to-r from-[#E15E92]/5 to-[#784BCB]/5 backdrop-blur-sm rounded-xl shadow-sm p-6" dir="rtl">
        <div className="flex flex-col space-y-6">
          <h2 className="text-2xl font-bold text-[#784BCB] flex items-center gap-2 border-r-4 border-[#40B8BF] pr-3 text-right relative">
            <span className="relative">
              אודות רדיו שבלולים
              <span className="absolute -bottom-1 right-0 w-full h-[2px] bg-gradient-to-r from-[#784BCB] to-[#40B8BF]"></span>
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md border-t-4 border-[#784BCB]">
              <h3 className="text-lg font-bold mb-3 text-[#784BCB] text-right">מי אנחנו</h3>
              <p className="text-[#393737] text-right font-content">
                רדיו שבלולים הוא תחנת רדיו אינטרנטית המשדרת מוזיקה איכותית, תוכניות מעניינות 
                וראיונות עם אמנים מובילים. אנחנו מאמינים ביצירת תוכן מקורי ואיכותי שמחבר בין המאזינים 
                למוזיקה טובה ולאנשים מעניינים.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md border-t-4 border-[#E15E92]">
              <h3 className="text-lg font-bold mb-3 text-[#E15E92] text-right">השידורים הבאים</h3>
              <ul className="space-y-3 text-[#393737]">
                <li className="flex justify-between border-b border-gray-100 pb-2 font-content">
                  <span>ימים א'-ה'</span>
                  <span className="font-medium">16:00 - 22:00</span>
                </li>
                <li className="flex justify-between border-b border-gray-100 pb-2 font-content">
                  <span>יום ו'</span>
                  <span className="font-medium">12:00 - 18:00</span>
                </li>
                <li className="flex justify-between font-content">
                  <span>שבת</span>
                  <span className="font-medium">15:00 - 20:00</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="h-1 bg-gradient-to-r from-[#784BCB] to-[#E15E92] mt-4 rounded-full opacity-50" />
      </section>

      {/* Display Window Section */}
      <section className="py-6 px-6 bg-gradient-to-r from-[#40B8BF]/5 to-[#E15E92]/5 backdrop-blur-sm rounded-xl shadow-sm relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#784BCB]/5 to-[#E15E92]/5 rounded-xl"></div>
          
        {/* Store-like header */}
        <div className="flex items-center justify-between mb-6 relative">
          <h2 className="text-2xl font-bold text-[#40B8BF] drop-shadow-md relative">
            <span className="relative">
              חלון תצוגה
              <span className="absolute -bottom-1 right-0 w-full h-[2px] bg-gradient-to-r from-[#E15E92] to-[#40B8BF]"></span>
            </span>
          </h2>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#E15E92]"></div>
            <div className="w-3 h-3 rounded-full bg-[#40B8BF]"></div>
            <div className="w-3 h-3 rounded-full bg-[#784BCB]"></div>
          </div>
        </div>
        
        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative">
          {/* Upcoming show feature */}
          <motion.div 
            whileHover={{ y: -5, scale: 1.02 }}
            className="bg-white/30 backdrop-blur-sm p-5 rounded-lg border border-[#784BCB]/20 shadow-md relative overflow-hidden group cursor-pointer"
            onClick={() => navigateToCardSection(upcomingShowsRef)}
          >
            <div className="absolute top-0 right-0 w-16 h-16 bg-[#784BCB] rotate-12 -translate-y-4 translate-x-4 opacity-10 rounded-md"></div>
            
            <Calendar className="text-[#784BCB] w-6 h-6 mb-3" />
            <h3 className="text-lg font-bold text-[#784BCB] mb-2">תוכניות קרובות</h3>
            <p className="text-[#393737]/70 text-sm font-content">
              אל תפספסו את התוכניות המיוחדות שלנו בשבוע הקרוב!
            </p>
            <div className="mt-4 text-xs font-medium flex justify-between items-center">
              <span className="text-[#784BCB]">השבוע</span>
              <span>פרק חדש בכל יום ב'</span>
            </div>
          </motion.div>
          
          {/* Latest episodes */}
          <motion.div 
            whileHover={{ y: -5, scale: 1.02 }}
            className="bg-white/30 backdrop-blur-sm p-5 rounded-lg border border-[#40B8BF]/20 shadow-md relative overflow-hidden group cursor-pointer"
            onClick={() => navigateToCardSection(vodRef)}
          >
            <div className="absolute top-0 right-0 w-16 h-16 bg-[#40B8BF] rotate-12 -translate-y-4 translate-x-4 opacity-10 rounded-md"></div>
            
            <Headphones className="text-[#40B8BF] w-6 h-6 mb-3" />
            <h3 className="text-lg font-bold text-[#40B8BF] mb-2">פרקים אחרונים</h3>
            <p className="text-[#393737]/70 text-sm font-content">
              האזינו לפרקים החמים ביותר מהשבועות האחרונים.
            </p>
            <div className="mt-4 text-xs font-medium flex justify-between items-center">
              <span className="text-[#40B8BF]">חדש!</span>
              <span>ראיונות, סטים וסיפורים מהאולפן</span>
            </div>
          </motion.div>
          
          {/* Featured artists */}
          <motion.div 
            whileHover={{ y: -5, scale: 1.02 }}
            className="bg-white/30 backdrop-blur-sm p-5 rounded-lg border border-[#E15E92]/20 shadow-md relative overflow-hidden group cursor-pointer"
            onClick={() => navigateToCardSection(featuredArtistsRef)}
          >
            <div className="absolute top-0 right-0 w-16 h-16 bg-[#E15E92] rotate-12 -translate-y-4 translate-x-4 opacity-10 rounded-md"></div>
            
            <Users className="text-[#E15E92] w-6 h-6 mb-3" />
            <h3 className="text-lg font-bold text-[#E15E92] mb-2">אמנים במוקד</h3>
            <p className="text-[#393737]/70 text-sm font-content">
              הכירו את האמנים המובילים שמככבים בתחנה שלנו.
            </p>
            <div className="mt-4 text-xs font-medium flex justify-between items-center">
              <span className="text-[#E15E92]">במוקד</span>
              <span>אמני בית, אורחים והפקות מקור</span>
            </div>
          </motion.div>
        </div>
        
        <motion.div 
          whileHover={{ y: -3 }}
          className="mt-6 text-center relative"
        >
          <button 
            onClick={() => vodRef.current?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#784BCB] to-[#E15E92] text-white rounded-md shadow-lg transition-all hover:shadow-xl app-button"
          >
            <Play className="w-4 h-4" />
            <span>לצפייה בארכיון</span>
          </button>
        </motion.div>
      </section>

      {/* VOD Section */}
      <section ref={vodRef} className="py-6 bg-gradient-to-r from-[#784BCB]/5 to-[#40B8BF]/5 backdrop-blur-sm rounded-xl shadow-sm p-6" dir="rtl">
        {/* Header with Search and Filter */}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 mb-8">
          <h2 className="text-2xl font-bold text-[#784BCB] drop-shadow-md border-r-4 border-[#40B8BF] pr-3 text-right flex items-center gap-2">
            <Play className="text-[#40B8BF]" />
            <span className="relative">
              הפרקים שלנו
              <span className="absolute -bottom-1 right-0 w-full h-[2px] bg-gradient-to-r from-[#784BCB] to-[#E15E92]"></span>
            </span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Bar */}
            <motion.div 
              className="relative"
              animate={isSearchFocused ? { scale: 1.02 } : { scale: 1 }}
            >
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder="חיפוש פרקים..."
                className="w-full sm:w-64 bg-white border border-[#784BCB]/30 text-[#393737] py-2 pr-10 pl-4 rounded-md
                          focus:outline-none focus:ring-2 focus:ring-[#784BCB]/50
                          placeholder-gray-500 transition-all duration-300 text-right"
              />
              {searchTerm ? (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#393737]/60
                            hover:text-[#784BCB] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#393737]/60" />
              )}
            </motion.div>

            {/* Show Filter */}
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.02 }}
            >
              <select 
                value={selectedShow?._id || ''} 
                onChange={(e) => setSelectedShow(shows.find(s => s._id === e.target.value) || null)}
                className="appearance-none bg-white border border-[#784BCB]/30 text-[#393737] py-2 pr-10 pl-4 rounded-md 
                          focus:outline-none focus:ring-2 focus:ring-[#784BCB]/50 w-full sm:w-auto text-right"
              >
                <option value="">כל התוכניות</option>
                {shows && shows.map(show => (
                  <option key={show._id} value={show._id}>{show.title}</option>
                ))}
              </select>
              <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#40B8BF]" />
            </motion.div>
          </div>
        </div>

        {/* Featured Episode */}
        {featuredEpisode && (
          <FeaturedEpisodeCard 
            episode={featuredEpisode} 
            formatDuration={formatDuration} 
          />
        )}

        {/* Episodes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedEpisodes.map((episode, index) => (
            <EpisodeCard 
              key={episode._id} 
              episode={episode} 
              index={index} 
              formatDuration={formatDuration} 
            />
          ))}
        </div>

        {/* Show more button */}
        {filteredEpisodes.length > visibleEpisodeCount && (
          <div className="text-center mt-8" dir="rtl">
            <button
              onClick={handleShowMoreEpisodes}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 rounded-md bg-gradient-to-l from-[#784BCB] to-[#E15E92] text-white font-medium app-button"
            >
              הצג עוד פרקים
            </button>
          </div>
        )}
      </section>

      {/* Upcoming Shows Section */}
      <section ref={upcomingShowsRef} className="py-6 bg-gradient-to-r from-[#784BCB]/5 to-[#E15E92]/5 backdrop-blur-sm rounded-xl shadow-sm p-6">
        <h2 className="text-2xl font-bold text-[#784BCB] drop-shadow-md mb-6 flex items-center gap-2">
          <Calendar className="text-[#40B8BF]" />
          <span className="relative">
            תוכניות קרובות
            <span className="absolute -bottom-1 right-0 w-full h-[2px] bg-gradient-to-r from-[#784BCB] to-[#40B8BF]"></span>
          </span>
        </h2>
        
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-[#784BCB]/5 to-transparent p-4 rounded-lg border border-[#784BCB]/10">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-[#784BCB]">סט פתיחת שבוע</h3>
              <span className="bg-[#784BCB] text-white text-xs px-2 py-1 rounded-full">יום ב'</span>
            </div>
            <p className="text-sm text-[#393737]/70 mb-2 font-content">ראיון עם אמן אורח מיוחד וסט מוזיקה ייחודי</p>
            <div className="flex justify-between text-xs text-[#393737]/70 font-content">
              <span>20:00</span>
              <span>משך: שעה וחצי</span>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-[#E15E92]/5 to-transparent p-4 rounded-lg border border-[#E15E92]/10">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-[#E15E92]">שיחות עם יוצרים</h3>
              <span className="bg-[#E15E92] text-white text-xs px-2 py-1 rounded-full">יום ד'</span>
            </div>
            <p className="text-sm text-[#393737]/70 mb-2 font-content">דיון מעמיק על יצירה ואמנות בעולם המודרני</p>
            <div className="flex justify-between text-xs text-[#393737]/70 font-content">
              <span>19:00</span>
              <span>משך: שעתיים</span>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-[#40B8BF]/5 to-transparent p-4 rounded-lg border border-[#40B8BF]/10">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-[#40B8BF]">Late Night Electronic</h3>
              <span className="bg-[#40B8BF] text-white text-xs px-2 py-1 rounded-full">יום ו'</span>
            </div>
            <p className="text-sm text-[#393737]/70 mb-2 font-content">סט מיוחד של מוזיקה אלקטרונית עכשווית</p>
            <div className="flex justify-between text-xs text-[#393737]/70 font-content">
              <span>22:00</span>
              <span>משך: 3 שעות</span>
            </div>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <button 
            onClick={() => window.open('https://calendar.google.com/calendar/render?action=TEMPLATE&text=רדיו%20שבלולים%20-%20תוכניות%20קרובות', '_blank')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#784BCB] text-white rounded-md hover:bg-[#6A3DB5] transition-colors app-button"
          >
            <Calendar className="w-4 h-4" />
            <span>הוסף ללוח השנה</span>
          </button>
        </div>
      </section>

      {/* Featured Artists Section */}
      <section ref={featuredArtistsRef} className="py-6 bg-gradient-to-r from-[#E15E92]/5 to-[#40B8BF]/5 backdrop-blur-sm rounded-xl shadow-sm p-6">
        <h2 className="text-2xl font-bold text-[#E15E92] drop-shadow-md mb-6 flex items-center gap-2">
          <Users className="text-[#40B8BF]" />
          <span className="relative">
            אמנים
            <span className="absolute -bottom-1 right-0 w-full h-[2px] bg-gradient-to-r from-[#E15E92] to-[#784BCB]"></span>
          </span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white/50 rounded-lg shadow-md overflow-hidden border border-[#E15E92]/10">
            <div className="h-40 bg-gradient-to-r from-[#784BCB] to-[#E15E92] flex items-center justify-center text-white">
              <span className="font-bold text-xl">במוקד השבוע</span>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-[#E15E92] mb-1">אמנית השבוע</h3>
              <p className="text-sm text-[#393737]/70 mb-3 font-content">יוצרת רב-תחומית עם יצירות פורצות דרך</p>
              <button 
                onClick={() => window.open('https://www.instagram.com/', '_blank')}
                className="w-full py-2 bg-[#E15E92] text-white rounded-md hover:bg-[#D04982] transition-colors"
              >
                פרופיל אמן
              </button>
            </div>
          </div>
          
          <div className="bg-white/50 rounded-lg shadow-md overflow-hidden border border-[#40B8BF]/10">
            <div className="h-40 bg-gradient-to-r from-[#40B8BF] to-[#784BCB] flex items-center justify-center text-white">
              <span className="font-bold text-xl">באולפן השבוע</span>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-[#40B8BF] mb-1">אמן אורח</h3>
              <p className="text-sm text-[#393737]/70 mb-3 font-content">מוזיקאי וזמר עם סגנון ייחודי ומקורי</p>
              <button 
                onClick={() => window.open('https://www.instagram.com/', '_blank')}
                className="w-full py-2 bg-[#40B8BF] text-white rounded-md hover:bg-[#35A4AA] transition-colors"
              >
                פרופיל אמן
              </button>
            </div>
          </div>
        </div>
        
        <div className="mt-8 bg-gradient-to-r from-[#784BCB]/10 to-[#E15E92]/10 rounded-lg p-4 border border-[#784BCB]/10">
          <h3 className="font-bold text-[#784BCB] mb-2">רוצים להיות האמן הבא שלנו?</h3>
          <p className="text-sm text-[#393737]/70 mb-4 font-content">אנחנו תמיד מחפשים אמנים חדשים להציג בתחנה. שלחו לנו הודעה כדי להציג את היצירות שלכם!</p>
          <button 
            onClick={() => window.location.href = '/contact'}
            className="px-4 py-2 bg-[#784BCB] text-white rounded-md hover:bg-[#6A3DB5] transition-colors app-button"
          >
            צרו קשר
          </button>
        </div>
      </section>
    </div>
  );
}

const shimmerAnimation = `
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.animate-shimmer {
  animation: shimmer 3s ease-in-out infinite;
}
`; 